<script setup>
import { computed, onMounted, onUnmounted, ref, nextTick } from 'vue';

const props = defineProps({
  align: { type: String, default: 'right' },
  width: { type: String, default: '48' },
  contentClasses: { type: String, default: 'py-1' },
  modelValue: { type: Boolean, default: undefined },
});

const emit = defineEmits(['update:modelValue', 'menuStateChange']);

const trigger = ref(null);
const internalOpen = ref(false);
const dropdownPosition = ref({ top: 0, left: 0 });

const open = computed({
  get: () => (props.modelValue !== undefined ? props.modelValue : internalOpen.value),
  set: (value) => {
    if (props.modelValue !== undefined) emit('update:modelValue', value);
    else internalOpen.value = value;
    emit('menuStateChange', value);
    if (value) updatePosition(); // Calculate position when opening
  },
});

// Calculate where the button is on the screen
const updatePosition = async () => {
  await nextTick();
  if (!trigger.value) return;

  const rect = trigger.value.getBoundingClientRect();
  const menuWidth = props.width === 'full' ? rect.width : parseInt(props.width) * 4;

  // Logic for Right vs Left alignment
  let left = rect.left;
  if (props.align === 'right') {
    left = rect.right - menuWidth;
  }

  dropdownPosition.value = {
    top: rect.bottom + window.scrollY,
    left: left + window.scrollX,
    width: props.width === 'full' ? `${rect.width}px` : '',
  };
};

const close = () => (open.value = false);

// Event Listeners for closing and repositioning
onMounted(() => {
  window.addEventListener('keydown', (e) => e.key === 'Escape' && close());
  window.addEventListener('scroll', updatePosition, true);
  window.addEventListener('resize', updatePosition);
});
</script>

<template>
  <div class="relative inline-block">
    <div ref="trigger" @click="open = !open" class="cursor-pointer">
      <slot name="trigger" />
    </div>

    <Teleport to="body">
      <div v-if="open" class="relative">
        <div class="fixed inset-0 z-dropdown" @click="close"></div>

        <Transition
          enter-active-class="transition ease-out duration-normal"
          enter-from-class="transform scale-95 opacity-0"
          enter-to-class="transform scale-100 opacity-100"
          leave-active-class="transition duration-75 ease-in"
          leave-from-class="transform scale-100 opacity-100"
          leave-to-class="transform scale-95 opacity-0"
        >
          <div
            v-show="open"
            class="fixed mt-2 rounded-md shadow-lg z-menu"
            :style="{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: dropdownPosition.width,
            }"
            @click="close"
          >
            <div
              class="rounded-md ring-1 ring-black ring-opacity-5 dark:ring-grey-700 bg-theme-paper"
              :class="[contentClasses, {'w-48': width == '48', 'w-44': width == '44' }]"
            >
              <slot name="content" />
            </div>
          </div>
        </Transition>
      </div>
    </Teleport>
  </div>
</template>
